import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { useAuth } from '../contexts/AuthContext'
import { useFamily } from '../contexts/FamilyContext'
import Layout from '../components/layout/Layout'
import SafeIcon from '../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'
import Button from '../components/ui/Button'
import Card from '../components/ui/Card'
import Avatar from '../components/ui/Avatar'
import ProgressBar from '../components/ui/ProgressBar'
import Badge from '../components/ui/Badge'
import TaskCard from '../components/tasks/TaskCard'
import CreateTaskModal from '../components/tasks/CreateTaskModal'
import TaskTemplateModal from '../components/tasks/TaskTemplateModal'
import AddChildModal from '../components/family/AddChildModal'
import EditChildModal from '../components/family/EditChildModal'
import DeleteChildModal from '../components/family/DeleteChildModal'
import ChildCard from '../components/family/ChildCard'
import FloatingAvatar from '../components/avatar/FloatingAvatar'
import RewardRequestWidget from '../components/dashboard/RewardRequestWidget'
import GamificationWidget from '../components/dashboard/GamificationWidget'
import QuickTaskSuggestions from '../components/tasks/QuickTaskSuggestions'

const { FiPlus, FiTrendingUp, FiUsers, FiStar, FiGift, FiClock, FiBookOpen, FiZap, FiEye, FiEyeOff } = FiIcons

const Dashboard = () => {
  const { family, addChild, updateChild, removeChild } = useAuth()
  const { tasks, completions, getChildPoints, getChildLevel, loading } = useFamily()
  const [showCreateTask, setShowCreateTask] = useState(false)
  const [showTaskTemplates, setShowTaskTemplates] = useState(false)
  const [showAddChild, setShowAddChild] = useState(false)
  const [showEditChild, setShowEditChild] = useState(false)
  const [showDeleteChild, setShowDeleteChild] = useState(false)
  const [selectedChild, setSelectedChild] = useState(null)
  const [showTaskSuggestions, setShowTaskSuggestions] = useState(tasks.length < 3)

  const todaysTasks = tasks.filter(task => {
    const today = new Date().toDateString()
    return !task.due_date || new Date(task.due_date).toDateString() >= today
  })

  const recentCompletions = completions
    .sort((a, b) => new Date(b.completed_at) - new Date(a.completed_at))
    .slice(0, 5)

  const familyStats = {
    totalTasks: tasks.length,
    completedToday: completions.filter(c => {
      const today = new Date().toDateString()
      return new Date(c.completed_at).toDateString() === today
    }).length,
    totalPoints: completions.reduce((sum, c) => sum + (c.points_earned || 0), 0),
    activeChildren: family?.children?.length || 0
  }

  const handleAddChild = async (childData) => {
    return await addChild(childData)
  }

  const handleEditChild = (child) => {
    setSelectedChild(child)
    setShowEditChild(true)
  }

  const handleDeleteChild = (child) => {
    setSelectedChild(child)
    setShowDeleteChild(true)
  }

  const handleChildUpdated = async (updatedChild) => {
    await updateChild(updatedChild.id, updatedChild)
  }

  const handleChildDeleted = async (childId) => {
    await removeChild(childId)
  }

  // Helper messages for floating avatar
  const helpMessages = [
    "Ready to create some tasks? 📝",
    "Check out the Task Library for 100+ pre-made tasks! 📚",
    "Don't forget to add rewards for motivation! 🎁",
    "How about adding a new child to the family? 👨‍👩‍👧‍👦",
    "Great job managing your family's chores! 🌟",
    "Try the age-appropriate task suggestions! 🎯",
    "Enable gamification for bonus points and multipliers! 🎮"
  ]

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">Loading your family dashboard...</p>
          </div>
        </div>
      </Layout>
    )
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                Welcome back, {family?.name || 'Family'}! 👋
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mt-1">
                Here's what's happening in your household today
              </p>
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowTaskTemplates(true)}
                className="flex items-center gap-2"
              >
                <SafeIcon icon={FiBookOpen} />
                Task Library
              </Button>
              <Button
                variant="primary"
                onClick={() => setShowCreateTask(true)}
                className="self-start sm:self-auto"
              >
                <SafeIcon icon={FiPlus} />
                Create Task
              </Button>
            </div>
          </div>
        </div>

        {/* Children Progress - Moved to top for emotional connection */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-8"
        >
          <Card>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-3">
                  <span className="text-3xl">👨‍👩‍👧‍👦</span>
                  Your Amazing Family
                </h2>
                <p className="text-gray-600 dark:text-gray-400 mt-1">
                  Watch your children grow and achieve their goals
                </p>
              </div>
              <Button
                variant="primary"
                size="sm"
                onClick={() => setShowAddChild(true)}
                className="flex items-center gap-2"
              >
                <SafeIcon icon={FiPlus} className="text-sm" />
                Add Child
              </Button>
            </div>

            {family?.children && family.children.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {family.children.map((child, index) => (
                  <motion.div
                    key={child.id}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.1 + (index * 0.1) }}
                  >
                    <ChildCard
                      child={child}
                      onEdit={handleEditChild}
                      onDelete={handleDeleteChild}
                      showActions={true}
                    />
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="text-6xl mb-6">👨‍👩‍👧‍👦</div>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-3">
                  Your Family Journey Starts Here!
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md mx-auto">
                  Add your first child to start building positive habits and celebrating their achievements together
                </p>
                <Button
                  variant="primary"
                  size="lg"
                  onClick={() => setShowAddChild(true)}
                  className="flex items-center gap-2 mx-auto"
                >
                  <SafeIcon icon={FiPlus} />
                  Add Your First Child
                </Button>
              </div>
            )}
          </Card>
        </motion.div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card className="text-center">
              <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center mx-auto mb-3">
                <SafeIcon icon={FiStar} className="text-white text-xl" />
              </div>
              <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">{familyStats.totalPoints}</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Total Points Earned</div>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Card className="text-center">
              <div className="w-12 h-12 bg-gradient-to-br from-success-500 to-success-600 rounded-xl flex items-center justify-center mx-auto mb-3">
                <SafeIcon icon={FiTrendingUp} className="text-white text-xl" />
              </div>
              <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">{familyStats.completedToday}</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Tasks Completed Today</div>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <Card className="text-center">
              <div className="w-12 h-12 bg-gradient-to-br from-secondary-500 to-secondary-600 rounded-xl flex items-center justify-center mx-auto mb-3">
                <SafeIcon icon={FiUsers} className="text-white text-xl" />
              </div>
              <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">{familyStats.activeChildren}</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Active Children</div>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <Card className="text-center">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center mx-auto mb-3">
                <SafeIcon icon={FiGift} className="text-white text-xl" />
              </div>
              <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">{familyStats.totalTasks}</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Available Tasks</div>
            </Card>
          </motion.div>
        </div>

        {/* Gamification Widget */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="mb-8"
        >
          <GamificationWidget />
        </motion.div>

        {/* Task Suggestions Toggle - Always visible */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="mb-6"
        >
          <div className="flex items-center justify-center">
            <Button
              variant={showTaskSuggestions ? "primary" : "outline"}
              size="sm"
              onClick={() => setShowTaskSuggestions(!showTaskSuggestions)}
              className="flex items-center gap-2"
            >
              <SafeIcon icon={showTaskSuggestions ? FiEyeOff : FiZap} />
              {showTaskSuggestions ? 'Hide Task Suggestions' : 'Show Task Suggestions'}
            </Button>
          </div>
        </motion.div>

        {/* Task Suggestions for New Users */}
        {showTaskSuggestions && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mb-8"
          >
            <div className="mb-4">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 text-center">
                {tasks.length === 0 ? 'Get Started with Popular Tasks' : 'Suggested Tasks'}
              </h2>
              <p className="text-gray-600 dark:text-gray-400 text-center text-sm mt-1">
                Age-appropriate task ideas to help you get started
              </p>
            </div>
            <QuickTaskSuggestions maxSuggestions={6} />
          </motion.div>
        )}

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Today's Tasks */}
          <div className="lg:col-span-2">
            <Card>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Today's Tasks</h2>
                <Badge variant="info" size="sm">
                  {todaysTasks.length} available
                </Badge>
              </div>

              {todaysTasks.length > 0 ? (
                <div className="space-y-4">
                  {todaysTasks.slice(0, 6).map((task) => (
                    <motion.div
                      key={task.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                    >
                      <TaskCard task={task} />
                    </motion.div>
                  ))}
                  {todaysTasks.length > 6 && (
                    <div className="text-center pt-4">
                      <Button variant="ghost" size="sm" className="text-gray-600 dark:text-gray-400">
                        View all {todaysTasks.length} tasks
                      </Button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="text-4xl mb-4">✨</div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">All caught up!</h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-4">No tasks scheduled for today</p>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => setShowCreateTask(true)}
                  >
                    <SafeIcon icon={FiPlus} className="text-sm" />
                    Create New Task
                  </Button>
                </div>
              )}
            </Card>
          </div>

          {/* Right Column Widgets */}
          <div className="space-y-6">
            {/* Reward Requests Widget */}
            <RewardRequestWidget />

            {/* Recent Activity */}
            <Card>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Recent Activity</h2>
                <SafeIcon icon={FiClock} className="text-gray-400 dark:text-gray-500" />
              </div>

              {recentCompletions.length > 0 ? (
                <div className="space-y-3">
                  {recentCompletions.map((completion, index) => (
                    <motion.div
                      key={completion.id}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
                    >
                      <div className="text-lg">🎉</div>
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {completion.children?.name} completed "{completion.tasks?.title}"
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          +{completion.points_earned} points • {new Date(completion.completed_at).toLocaleDateString()}
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6">
                  <div className="text-2xl mb-2">📝</div>
                  <p className="text-gray-600 dark:text-gray-400 text-sm">No recent activity</p>
                </div>
              )}
            </Card>

            {/* Quick Task Actions */}
            <Card className="bg-gradient-to-r from-primary-50 to-secondary-50 dark:from-primary-900/20 dark:to-secondary-900/20">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
                Quick Actions
              </h3>
              <div className="space-y-2">
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full justify-start"
                  onClick={() => setShowTaskTemplates(true)}
                >
                  <SafeIcon icon={FiBookOpen} />
                  Browse 100+ Task Templates
                </Button>
              </div>
            </Card>
          </div>
        </div>

        {/* Modals */}
        <CreateTaskModal
          isOpen={showCreateTask}
          onClose={() => setShowCreateTask(false)}
        />

        <TaskTemplateModal
          isOpen={showTaskTemplates}
          onClose={() => setShowTaskTemplates(false)}
        />

        <AddChildModal
          isOpen={showAddChild}
          onClose={() => setShowAddChild(false)}
          onAddChild={handleAddChild}
        />

        <EditChildModal
          isOpen={showEditChild}
          onClose={() => {
            setShowEditChild(false)
            setSelectedChild(null)
          }}
          child={selectedChild}
          onChildUpdated={handleChildUpdated}
        />

        <DeleteChildModal
          isOpen={showDeleteChild}
          onClose={() => {
            setShowDeleteChild(false)
            setSelectedChild(null)
          }}
          child={selectedChild}
          onChildDeleted={handleChildDeleted}
        />

        {/* Floating Helper Avatar */}
        <FloatingAvatar
          character="🤖"
          position="bottom-right"
          messages={helpMessages}
          isVisible={true}
          onMessageClick={(message) => {
            console.log('Helper message clicked:', message)
          }}
        />
      </div>
    </Layout>
  )
}

export default Dashboard